import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createWhatsappTemplate,
  deleteWhatsappTemplate,
  getWhatsappTemplates,
  refreshWhatsappTemplateStatus,
  sendTestWhatsappTemplate,
  submitWhatsappTemplateForReview,
  updateWhatsappTemplate,
} from "api/bMarketingApi";

export function normalizeWhatsappTemplate(template) {
  const headerType = template.header_type || "none";

  return {
    ...template,
    databaseId: template.id,
    id: template.template_id,
    whatsappConfig: template.whatsapp_config ? String(template.whatsapp_config) : "",
    language: template.language || "",
    headerEnabled: Boolean(template.header_enabled),
    headerType,
    headerText: template.header_text || "",
    headerMediaLabel: template.header_attachment_name || "",
    headerAttachment: template.header_attachment_url
      ? {
          name: template.header_attachment_name || "Header attachment",
          type: headerType === "document" ? "application/pdf" : "",
          url: template.header_attachment_url,
        }
      : null,
    body: template.body || "",
    footerEnabled: Boolean(template.footer_enabled),
    footerText: template.footer_text || "",
    buttons: Array.isArray(template.buttons) ? template.buttons : [],
    reviewStatus: template.publish_status || "draft",
    qualityScore: template.quality_score || "",
    metaTemplateId: template.meta_template_id || "",
    isLockedForEditing: Boolean(template.is_locked_for_editing),
  };
}

export function useMarketingWhatsappTemplates(organisationId) {
  const queryClient = useQueryClient();

  const { data: whatsappTemplates, isLoading, error } = useQuery({
    queryKey: ["whatsappTemplates", organisationId],
    queryFn: () => getWhatsappTemplates(organisationId).then((res) => res.data.map(normalizeWhatsappTemplate)),
    enabled: !!organisationId,
  });

  const createMutation = useMutation({
    mutationFn: (data) => createWhatsappTemplate(data).then((res) => normalizeWhatsappTemplate(res.data)),
    onSuccess: () => {
      queryClient.invalidateQueries(["whatsappTemplates", organisationId]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ templateId, data }) => updateWhatsappTemplate(templateId, data).then((res) => normalizeWhatsappTemplate(res.data)),
    onSuccess: () => {
      queryClient.invalidateQueries(["whatsappTemplates", organisationId]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWhatsappTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(["whatsappTemplates", organisationId]);
    },
  });

  const submitForReviewMutation = useMutation({
    mutationFn: ({ templateId, data }) => submitWhatsappTemplateForReview(templateId, data).then((res) => normalizeWhatsappTemplate(res.data)),
    onSettled: () => {
      queryClient.invalidateQueries(["whatsappTemplates", organisationId]);
    },
  });

  const refreshStatusMutation = useMutation({
    mutationFn: (templateId) => refreshWhatsappTemplateStatus(templateId).then((res) => normalizeWhatsappTemplate(res.data)),
    onSettled: () => {
      queryClient.invalidateQueries(["whatsappTemplates", organisationId]);
    },
  });

  const sendTestMutation = useMutation({
    mutationFn: ({ templateId, data }) => sendTestWhatsappTemplate(templateId, data),
  });

  return {
    whatsappTemplates: whatsappTemplates || [],
    isLoading,
    error,
    createTemplate: createMutation.mutate,
    createTemplateAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateTemplate: updateMutation.mutate,
    updateTemplateAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteTemplate: deleteMutation.mutate,
    deleteTemplateAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    submitTemplateForReview: submitForReviewMutation.mutate,
    submitTemplateForReviewAsync: submitForReviewMutation.mutateAsync,
    isSubmittingForReview: submitForReviewMutation.isPending,
    refreshTemplateStatus: refreshStatusMutation.mutate,
    refreshTemplateStatusAsync: refreshStatusMutation.mutateAsync,
    isRefreshingStatus: refreshStatusMutation.isPending,
    sendTestTemplate: sendTestMutation.mutate,
    sendTestTemplateAsync: sendTestMutation.mutateAsync,
    isSendingTest: sendTestMutation.isPending,
  };
}
